import { useEffect, useState, useMemo, useRef } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { CREATE_METRIC_REPORT, GET_ATLAS_RELEASE_STATUS, GET_GANTT, GET_JIRA_STATUS, GET_METRICS, GET_PROJECTS, GET_REPORTING_AVAILABILITY, GET_REQUIREMENTS, GET_STATS, SYNC_JIRA, UPDATE_METRIC_TARGETS, UPDATE_PROJECT_STATUS } from "@/graphql/queries";
import { mapToSnakeCase } from "@/graphql/mappers";
import { useApp } from "@/context/AppContext";
import { DonutChart } from "@/components/DonutChart";
import { AtlasReleasePanel } from "@/components/AtlasReleasePanel";
import { MetricsReportContent } from "@/components/MetricsReportContent";
import type { GanttRow, MetricReport, MetricReportMode, Project, Requirement } from "@/types";

export function Dashboard() {
  const { navigateTo, authRole, impersonatedRole } = useApp();
  const [activeTab, setActiveTab] = useState<"overview" | "metrics" | "gantt">("overview");
  const [gapSearch, setGapSearch] = useState("");

  const { data: statsRaw } = useQuery<any>(GET_STATS, { fetchPolicy: "network-only" });
  const { data: reqsRaw } = useQuery<any>(GET_REQUIREMENTS, { fetchPolicy: "network-only" });
  const { data: metricsRaw, refetch: refetchMetrics } = useQuery<any>(GET_METRICS, { fetchPolicy: "network-only" });
  const { data: projectsRaw, refetch: refetchProjects } = useQuery<any>(GET_PROJECTS);
  const { data: ganttRaw } = useQuery<any>(GET_GANTT, { skip: activeTab !== "gantt" });

  const stats = statsRaw ? mapToSnakeCase(statsRaw.stats) : undefined;
  const requirements: Requirement[] = reqsRaw ? mapToSnakeCase(reqsRaw.requirements) : [];
  const metrics = metricsRaw ? mapToSnakeCase(metricsRaw.metrics) : undefined;
  const projects: Project[] = projectsRaw ? mapToSnakeCase(projectsRaw.projects.projects) : [];
  const project = projects.find(item => item.id === stats?.project_id);
  const ganttData = ganttRaw ? mapToSnakeCase(ganttRaw.gantt) : undefined;

  const leafReqs = requirements.filter((r: Requirement) => r.level === "implementation");
  const gapReqs = leafReqs.filter((r: Requirement) => {
    const hasTests = r.tests && r.tests.length > 0;
    return !hasTests;
  });
  const filteredGapReqs = useMemo(() => {
    if (!gapSearch.trim()) return gapReqs;
    const q = gapSearch.toLowerCase();
    return gapReqs.filter(r => r.id.toLowerCase().includes(q) || (r.description || r.title || "").toLowerCase().includes(q));
  }, [gapReqs, gapSearch]);

  const cc = stats?.completion_counts || {};
  const disciplines = stats?.disciplines || [];
  const correctnessIssues = [
    { label: "Missing Active Drivers", value: stats?.missing_active_drivers_count ?? 0 },
    { label: "Missing Implementation Tests", value: stats?.missing_implementation_tests_count ?? 0 },
    { label: "Grouping With Tests", value: stats?.grouping_with_tests_count ?? 0 },
    { label: "Grouping With Source Drivers", value: stats?.grouping_with_source_drivers_count ?? 0 },
    { label: "System Without Source Drivers", value: stats?.system_without_source_drivers_count ?? 0 },
    { label: "System Without Driven Children", value: stats?.system_without_driven_children_count ?? 0 },
  ];

  const disciplineData = disciplines.map((d: string): { discipline: string; total: number; complete: number; partial: number; incomplete: number } => {
    const reqs = leafReqs.filter((r: Requirement) => r.discipline === d);
    const complete = reqs.filter((r: Requirement) => r.completion === "Derived").length;
    const partial = reqs.filter((r: Requirement) => r.completion === "Partial").length;
    const incomplete = reqs.length - complete - partial;
    return { discipline: d, total: reqs.length, complete, partial, incomplete };
  });

  const autoPct = metrics ? Math.round(metrics.automation * 100) : 0;

  return (
    <div className="flex-1 p-6 flex flex-col overflow-auto w-full min-h-0" data-testid="dashboard">
      {/* Tab bar */}
      <div className="flex mb-6 border-b border-border shrink-0">
        <button className={`px-6 py-2.5 border-none bg-transparent text-text2 text-sm font-medium cursor-pointer border-b-2 border-transparent transition-colors duration-[120ms] hover:text-text${activeTab === "overview" ? " text-accent border-b-accent" : ""}`} onClick={() => setActiveTab("overview")}>Overview</button>
        <button className={`px-6 py-2.5 border-none bg-transparent text-text2 text-sm font-medium cursor-pointer border-b-2 border-transparent transition-colors duration-[120ms] hover:text-text${activeTab === "metrics" ? " text-accent border-b-accent" : ""}`} onClick={() => setActiveTab("metrics")}>Metrics</button>
        <button className={`px-6 py-2.5 border-none bg-transparent text-text2 text-sm font-medium cursor-pointer border-b-2 border-transparent transition-colors duration-[120ms] hover:text-text${activeTab === "gantt" ? " text-accent border-b-accent" : ""}`} onClick={() => setActiveTab("gantt")}>Gantt</button>
      </div>

      {/* Overview */}
      {activeTab === "overview" && (
        <div>
          <div className="mb-6">
            <h2 className="text-[22px] text-accent mb-1">{stats?.project_name || stats?.project_id || "Dashboard"}</h2>
            <div className="text-[13px] text-text2">Snapshot: {stats?.snapshot_date || "Latest"}</div>
          </div>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 mb-7">
            <DashCard value={stats?.total ?? 0} label="Total Requirements" />
            <DashCard value={stats?.active_requirement_count ?? 0} label="Active Requirements" valueColor="text-green" />
            <DashCard value={stats?.inactive_requirement_count ?? 0} label="Inactive Requirements" valueColor={(stats?.inactive_requirement_count ?? 0) > 0 ? "text-yellow" : "text-green"} />
            <DashCard value={stats?.correct_requirement_count ?? 0} label="Correct Requirements" valueColor="text-green" />
            <DashCard value={stats?.incorrect_requirement_count ?? 0} label="Incorrect Requirements" valueColor={(stats?.incorrect_requirement_count ?? 0) > 0 ? "text-red" : "text-green"} />
            {stats?.levels?.map((lvl: string) => (
              <DashCard key={lvl} value={stats?.level_counts?.[lvl] ?? 0} label={lvl} />
            ))}
            <DashCard value={cc.complete ?? 0} label="Complete" valueColor="text-green" />
            <DashCard value={cc.partial ?? 0} label="Partial" valueColor="text-yellow" />
            <DashCard value={cc.incomplete ?? 0} label="Incomplete" valueColor="text-red" />
            {metrics && (
              <DashCard value={`${autoPct}%`} label="Automation" valueColor={autoPct >= 70 ? "text-green" : autoPct >= 40 ? "text-yellow" : "text-red"} />
            )}
          </div>

          <AtlasReleasePanel projectId={stats?.project_id || ""} />

          <div className="mb-7" data-testid="correctness-dashboard">
            <h3 className="text-[13px] uppercase text-text2 tracking-wide mb-3">Correctness Issues</h3>
            <p className="text-xs text-text2 mb-3">
              Driver graph checks validate stored requirement levels against active requirement drivers, source drivers,
              driven requirements, and linked tests.
            </p>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
              {correctnessIssues.map(issue => (
                <DashCard
                  key={issue.label}
                  value={issue.value}
                  label={issue.label}
                  valueColor={issue.value > 0 ? "text-red" : "text-green"}
                />
              ))}
            </div>
          </div>

          {/* Discipline breakdown */}
          {disciplineData.length > 0 && (
            <div className="mb-7">
              <h3 className="text-[13px] uppercase text-text2 tracking-wide mb-3">Completion by Discipline</h3>
              <div className="flex gap-4 mb-2 text-xs text-text2">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-green" />Complete</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-yellow" />Partial</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-red" />Incomplete</span>
              </div>
              {disciplineData.map((d: { discipline: string; total: number; complete: number; partial: number; incomplete: number }) => (
                <div key={d.discipline} className="flex items-center gap-2.5 mb-2 text-[13px]">
                  <div className="w-[120px] text-text2 text-right shrink-0">{d.discipline}</div>
                  <div className="flex-1 h-[22px] bg-bg rounded overflow-hidden flex">
                    {d.total > 0 && (
                      <>
                        {d.complete > 0 && <div className="h-full transition-[width] duration-300 bg-green" style={{ width: `${(d.complete / d.total) * 100}%` }} />}
                        {d.partial > 0 && <div className="h-full transition-[width] duration-300 bg-yellow" style={{ width: `${(d.partial / d.total) * 100}%` }} />}
                        {d.incomplete > 0 && <div className="h-full transition-[width] duration-300 bg-red" style={{ width: `${(d.incomplete / d.total) * 100}%` }} />}
                      </>
                    )}
                  </div>
                  <div className="w-[50px] text-text font-semibold">{d.complete}/{d.total}</div>
                </div>
              ))}
            </div>
          )}

          {/* Gaps */}
          {gapReqs.length > 0 && (
            <div className="mb-7">
              <h3 className="text-[13px] uppercase text-text2 tracking-wide mb-3">Requirements Without Tests ({gapReqs.length})</h3>
              <input
                type="text"
                placeholder="Filter by ID or description..."
                value={gapSearch}
                onChange={e => setGapSearch(e.target.value)}
                className="w-full mb-2 px-2.5 py-1.5 text-[13px] bg-bg2 border border-border rounded text-text placeholder:text-text2 outline-none focus:border-accent"
                data-testid="gap-search"
              />
              <div>
                {filteredGapReqs.map(r => (
                  <div key={r.id} className="px-2.5 py-1.5 text-[13px] cursor-pointer border-b border-border transition-colors duration-150 hover:bg-bg3" onClick={() => navigateTo("requirements", r.id)}>
                    <span className="text-accent font-semibold">{r.id}</span>
                    <span className="text-text2 ml-2">{r.description || r.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Metrics tab */}
      {activeTab === "metrics" && metrics && (
        <MetricsTab
          metrics={metrics}
          requirements={requirements}
          navigateTo={navigateTo}
          projectName={stats?.project_name || stats?.project_id || ""}
          projectId={stats?.project_id || ""}
          project={project}
          canEditStatus={authRole === "admin" && !impersonatedRole}
          canSetTargets={authRole === "admin" && !impersonatedRole}
          refetchMetrics={refetchMetrics}
          refetchProjects={refetchProjects}
        />
      )}

      {/* Gantt tab */}
      {activeTab === "gantt" && <GanttTab data={ganttData} navigateTo={navigateTo} />}
    </div>
  );
}

function DashCard({ value, label, valueColor }: { value: number | string; label: string; valueColor?: string }) {
  return (
    <div className="bg-transparent border border-border rounded-default p-5 cursor-pointer transition-[border-color,background] duration-150 hover:border-border2 hover:bg-bg2">
      <div className={`text-[28px] font-bold text-accent mb-1${valueColor ? ` ${valueColor}` : ""}`}>{value}</div>
      <div className="text-xs text-text2 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function MetricCard({ title, subtitle, description, target, explanation, value, color, active, onMoreInfo }: { title: string; subtitle: string; description: string; target?: number | null; explanation: string; value: number; color: string; active?: boolean; onMoreInfo?: () => void }) {
  const pct = Math.round(value * 100);
  const targetPct = target === null || target === undefined ? null : Math.round(target * 100);
  return (
    <div className="bg-bg2 border border-border rounded-[10px] p-5 flex flex-col items-center text-center">
      <div>
        <h4 className="mb-1.5 text-text text-[15px] mt-0">{title}</h4>
        <p className="text-accent text-xs font-medium m-0 mb-1">{subtitle}</p>
        <p className="text-text2 text-xs leading-snug m-0 mb-1 font-semibold">
          {description}{targetPct !== null ? `. Target: ${targetPct}%.` : ""}
        </p>
      </div>
      <div className="mt-4 mb-2">
        <DonutChart value={value} color={color} target={target} />
      </div>
      <div className="text-lg font-bold" style={{ color }}>{pct}%</div>
      <p className="text-text2 text-[11px] leading-snug m-0 mt-3">{explanation}</p>
      {onMoreInfo && (
        <button
          type="button"
          onClick={onMoreInfo}
          className={`mt-[10px] h-[22px] px-2 bg-bg border border-border rounded-default text-[11px] cursor-pointer transition-colors duration-[120ms] hover:border-accent hover:text-accent flex items-center justify-center${active ? " border-accent text-accent" : " text-text"}`}
          aria-pressed={active}
          aria-label={`${title} more info`}
        >
          More info
        </button>
      )}
    </div>
  );
}

// ---- Metrics Tab ----

type MetricDetailKey = "translation" | "incorporation" | "completion" | "automation";

type MetricDetail = {
  title: string;
  summary: string;
  empty: string;
  items: Requirement[];
};

function releaseUpdatedDate(timestamp: string): string {
  const parts = new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date(timestamp));
  const part = (type: string) => parts.find(item => item.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function MetricsTab({ metrics, requirements, navigateTo, projectName, projectId, project, canEditStatus, canSetTargets, refetchMetrics, refetchProjects }: { metrics: any; requirements: Requirement[]; navigateTo: (view: import("@/types").ViewName, id: string | null) => void; projectName: string; projectId: string; project?: Project; canEditStatus: boolean; canSetTargets: boolean; refetchMetrics: () => Promise<unknown>; refetchProjects: () => Promise<unknown> }) {
  const [showDisciplines, setShowDisciplines] = useState(false);
  const [activeDetail, setActiveDetail] = useState<MetricDetailKey | null>(null);
  const [showTargetForm, setShowTargetForm] = useState(false);
  const [targetForm, setTargetForm] = useState({ translation: "", incorporation: "", completion: "", automation: "" });
  const [targetError, setTargetError] = useState("");
  const [jiraSyncMessage, setJiraSyncMessage] = useState("");
  const [updateMetricTargets] = useMutation<any>(UPDATE_METRIC_TARGETS);
  const [syncJira] = useMutation<any>(SYNC_JIRA);
  const [jiraSyncing, setJiraSyncing] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [statusError, setStatusError] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [updateProjectStatus] = useMutation<any>(UPDATE_PROJECT_STATUS);
  const { data: releasesData, loading: releasesLoading, error: releasesError } = useQuery<{
    atlasReleaseStatus: Array<{ id: string; name: string; releaseTag: string | null; releasePublishedAt: string | null; releaseUrl: string | null }>;
  }>(GET_ATLAS_RELEASE_STATUS, { variables: { projectId }, skip: !projectId, fetchPolicy: "network-only" });
  const { data: jiraStatusData, refetch: refetchJiraStatus, startPolling, stopPolling } = useQuery<any>(GET_JIRA_STATUS, {
    fetchPolicy: "network-only",
  });
  const jiraStatus = jiraStatusData?.jiraStatus;
  const jiraCycleActive = jiraStatus?.status === "in_progress" || jiraStatus?.status === "retry_later";
  const lastJiraSync = jiraStatus?.lastAttemptAt || jiraStatus?.lastSuccessAt;
  const lastJiraSyncLabel = lastJiraSync
    ? new Date(lastJiraSync).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "never";

  useEffect(() => {
    setEditingStatus(false);
    setStatusText(project?.status_text || "");
    setStatusError("");
    setReportOpen(false);
  }, [projectId, project?.status_text]);

  useEffect(() => {
    if (jiraStatus?.status === "in_progress") startPolling(5000);
    else stopPolling();
    return stopPolling;
  }, [jiraStatus?.status, startPolling, stopPolling]);

  const disciplines = metrics.disciplines || {};
  const discNames = Object.keys(disciplines).sort();
  const activeRequirements = requirements.filter(r => (r.active_driver_count || 0) > 0);
  const hasTests = (r: Requirement) => (r.tests || []).length > 0;
  const hasDrivenRequirements = (r: Requirement) => (r.driven_child_count || 0) > 0;
  const isLeaf = (r: Requirement) => !hasDrivenRequirements(r);
  const testIsComplete = (t: NonNullable<Requirement["tests"]>[number]) => {
    const status = t.test_status || "";
    if (status) return status === "done" || status === "complete" || status === "completed";
    const description = (t.description || "").trim().toUpperCase();
    return description !== "" && description !== "TBD";
  };
  const testIsAutomated = (t: NonNullable<Requirement["tests"]>[number]) => t.type === "automated" || t.type === "pipeline";
  const metricDetails: Record<MetricDetailKey, MetricDetail> = {
    translation: {
      title: "Translation gaps",
      summary: "System requirements with no driven requirements and no linked tests.",
      empty: "All system requirements have driven requirements or linked tests.",
      items: activeRequirements.filter(r => r.level === "system" && !hasDrivenRequirements(r) && !hasTests(r)),
    },
    incorporation: {
      title: "Incorporation gaps",
      summary: "Leaf requirements that are testable but do not have any linked tests.",
      empty: "All leaf requirements have at least one linked test.",
      items: activeRequirements.filter(r => isLeaf(r) && !hasTests(r)),
    },
    completion: {
      title: "Test completion gaps",
      summary: "Requirements with linked tests where one or more tests are not complete.",
      empty: "All linked tests are complete for every tested requirement.",
      items: activeRequirements.filter(r => isLeaf(r) && hasTests(r) && !(r.tests || []).every(testIsComplete)),
    },
    automation: {
      title: "Automation gaps",
      summary: "Requirements with linked tests where one or more tests are not automated or pipeline tests.",
      empty: "Every tested requirement has only automated or pipeline tests.",
      items: activeRequirements.filter(r => isLeaf(r) && hasTests(r) && !(r.tests || []).every(testIsAutomated)),
    },
  };

  const toggleDetail = (key: MetricDetailKey) => {
    setActiveDetail(current => current === key ? null : key);
  };

  const metricTarget = (key: MetricDetailKey) => metrics[`${key}_target`];
  const targetText = (key: MetricDetailKey) => {
    const target = metricTarget(key);
    return target === null || target === undefined ? "" : `${Math.round(target * 100)}%`;
  };

  const openTargetForm = () => {
    setTargetForm({
      translation: targetText("translation").replace("%", ""),
      incorporation: targetText("incorporation").replace("%", ""),
      completion: targetText("completion").replace("%", ""),
      automation: targetText("automation").replace("%", ""),
    });
    setTargetError("");
    setShowTargetForm(true);
  };

  const parseTarget = (value: string) => {
    if (!value.trim()) return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
      throw new Error("Targets must be between 0 and 100 percent.");
    }
    return parsed / 100;
  };

  const saveTargets = async () => {
    try {
      setTargetError("");
      await updateMetricTargets({
        variables: {
          translation: parseTarget(targetForm.translation),
          incorporation: parseTarget(targetForm.incorporation),
          completion: parseTarget(targetForm.completion),
          automation: parseTarget(targetForm.automation),
        },
      });
      await refetchMetrics();
      setShowTargetForm(false);
    } catch (error) {
      setTargetError(error instanceof Error ? error.message : "Could not save targets.");
    }
  };

  const updateFromJira = async () => {
    setJiraSyncing(true);
    try {
      setJiraSyncMessage("");
      let result;
      do {
        const { data } = await syncJira();
        result = data?.syncJira;
        if (result?.status === "in_progress") {
          setJiraSyncMessage(`Processed ${result.processed ?? 0} of ${result.totalKeys ?? 0} Jira tickets...`);
        }
      } while (result?.status === "in_progress");
      await refetchJiraStatus();
      if (result?.status === "locked") {
        setJiraSyncMessage("Another Jira update is currently running.");
      } else if (result?.status === "retry_later" || result?.status === "failed") {
        setJiraSyncMessage("Jira update is waiting to retry after a temporary Jira error.");
      } else {
        setJiraSyncMessage(`Updated ${result?.synced ?? 0} Jira tickets; refreshing metrics...`);
        await refetchMetrics();
        setJiraSyncMessage(`Updated ${result?.synced ?? 0} of ${result?.totalKeys ?? 0} Jira tickets${result?.errors ? `; ${result.errors} failed` : ""}.`);
      }
    } catch (error) {
      setJiraSyncMessage(error instanceof Error ? error.message : "Could not update from Jira.");
    } finally {
      setJiraSyncing(false);
    }
  };

  const saveStatus = async () => {
    setStatusSaving(true);
    setStatusError("");
    try {
      await updateProjectStatus({ variables: { projectId, statusText: statusText.trim(), expectedVersion: project?.status_version ?? 0 } });
      await refetchProjects();
      setEditingStatus(false);
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : "Could not update project status.");
      await refetchProjects();
    } finally {
      setStatusSaving(false);
    }
  };

  return (
    <div>
      <section className="mb-5" data-testid="project-status">
        <h2 className="m-0 text-xl text-text">{projectName}</h2>
        {!editingStatus ? (
          <div className="mt-1 flex items-center gap-3 text-sm text-text2">
            <span>Project status: <strong className="text-text">{project?.status_text || "Not set"}</strong></span>
            {canEditStatus && <button type="button" onClick={() => setEditingStatus(true)} className="border-0 bg-transparent p-0 text-xs text-accent cursor-pointer">Edit status</button>}
          </div>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="project-status-input">Project status</label>
            <input id="project-status-input" value={statusText} maxLength={100} onChange={event => setStatusText(event.target.value)} className="px-2.5 py-1.5 bg-bg2 border border-border rounded-default text-text text-sm" />
            <button type="button" disabled={statusSaving} onClick={saveStatus} className="px-3 py-1.5 bg-accent border border-accent rounded-default text-white text-xs cursor-pointer disabled:opacity-60">{statusSaving ? "Saving..." : "Save"}</button>
            <button type="button" disabled={statusSaving} onClick={() => { setEditingStatus(false); setStatusText(project?.status_text || ""); setStatusError(""); }} className="px-3 py-1.5 bg-bg2 border border-border rounded-default text-text text-xs cursor-pointer">Cancel</button>
            {statusError && <span role="alert" className="text-xs text-red">{statusError}</span>}
          </div>
        )}
        <div className="mt-2 text-xs text-text2" data-testid="project-release-status">
          <span className="font-semibold">Latest releases:</span>{" "}
          {releasesLoading ? "Loading..." : releasesError ? "Unavailable" : !releasesData?.atlasReleaseStatus.length ? "No SKUs configured" : (
            <span className="inline-flex flex-wrap gap-x-4 gap-y-1">
              {releasesData.atlasReleaseStatus.map(sku => (
                <span key={sku.id}>
                  {sku.name}: {sku.releaseTag && sku.releaseUrl ? (
                    <a href={sku.releaseUrl} target="_blank" rel="noopener noreferrer" className="text-accent">{sku.releaseTag}{sku.releaseTag.includes("-vargus-test-") ? " (test release)" : ""}</a>
                  ) : "Not published"} · Last updated: {sku.releasePublishedAt ? releaseUpdatedDate(sku.releasePublishedAt) : "Not available"}
                </span>
              ))}
            </span>
          )}
        </div>
      </section>
      <div className="mb-7">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[13px] uppercase text-text2 tracking-wide m-0">Traceability Metrics</h3>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-[11px] text-text2 cursor-pointer">
              <input type="checkbox" checked={showDisciplines} onChange={e => setShowDisciplines(e.target.checked)} />
              Show discipline breakdown
            </label>
            {canSetTargets && <button onClick={openTargetForm} className="px-3 py-1.5 bg-bg2 border border-border rounded-default text-text text-[11px] cursor-pointer transition-colors duration-[120ms] hover:border-accent hover:text-accent">Set targets</button>}
            <button type="button" onClick={() => setReportOpen(true)} className="px-3 py-1.5 bg-accent border border-accent rounded-default text-white text-[11px] cursor-pointer">Report</button>
            {jiraStatus && (
              <div
                className="text-[11px] text-text2 whitespace-nowrap"
                data-testid="jira-operational-status"
                title={jiraStatus.lastError || `Last successful sync: ${jiraStatus.lastSuccessAt || "never"}`}
              >
                Jira: <span className={jiraStatus.lastError ? "text-red" : jiraCycleActive ? "text-yellow" : "text-green"}>
                  {jiraStatus.configured ? `Last sync ${lastJiraSyncLabel}` : "not configured"}
                </span>
                {jiraCycleActive && ` ${jiraStatus.processed}/${jiraStatus.mappedTests}`}
                {!jiraCycleActive && jiraStatus.configured && ` · ${jiraStatus.cachedTickets}/${jiraStatus.mappedTests} cached`}
                {jiraStatus.warnings > 0 && ` · ${jiraStatus.warnings} warning${jiraStatus.warnings === 1 ? "" : "s"}`}
              </div>
            )}
            <button type="button" onClick={updateFromJira} disabled={jiraSyncing} className="px-3 py-1.5 bg-bg2 border border-border rounded-default text-text text-[11px] cursor-pointer transition-colors duration-[120ms] hover:border-accent hover:text-accent disabled:cursor-wait disabled:opacity-60">{jiraSyncing ? "Updating from Jira..." : jiraCycleActive ? "Resume Jira update" : "Update from Jira"}</button>
            <button onClick={() => setReportOpen(true)} className="px-3 py-1.5 bg-bg2 border border-border rounded-default text-text text-[11px] cursor-pointer transition-colors duration-[120ms] hover:border-accent hover:text-accent">Export CSV</button>
          </div>
        </div>
        <p className="text-text2 text-[13px] mt-1 mb-3">Key metrics measuring requirements traceability coverage across the project.</p>
        {jiraSyncMessage && <div className="text-xs text-text2 mb-3" role="status">{jiraSyncMessage}</div>}
        {showTargetForm && (
          <div className="mb-4 border border-border rounded-[10px] bg-bg2 p-4" data-testid="metric-target-form">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
              {(["translation", "incorporation", "completion", "automation"] as MetricDetailKey[]).map(key => (
                <label key={key} className="text-[11px] text-text2 uppercase tracking-wide">
                  {key === "completion" ? "Test Completion" : key} (%)
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={targetForm[key]}
                    onChange={e => setTargetForm(current => ({ ...current, [key]: e.target.value }))}
                    className="mt-1 w-full px-2 py-1.5 bg-bg border border-border rounded-default text-text text-xs outline-none focus:border-accent"
                    aria-label={`${key} target`}
                  />
                </label>
              ))}
            </div>
            {targetError && <div className="mt-2 text-xs text-red">{targetError}</div>}
            <div className="mt-3 flex gap-2 justify-end">
              <button type="button" onClick={() => setShowTargetForm(false)} className="px-3 py-1.5 bg-bg border border-border rounded-default text-text text-[11px] cursor-pointer hover:border-border2">Cancel</button>
              <button type="button" onClick={saveTargets} className="px-3 py-1.5 bg-accent border border-accent rounded-default text-white text-[11px] cursor-pointer">Save targets</button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-5 mt-4 w-full">
          <MetricCard
            title="Translation"
            subtitle="Have all system requirements been broken down?"
            description={`${metrics.customer_reqs_with_product} / ${metrics.total_customer_reqs} system requirements with children or tests`}
            target={metrics.translation_target}
            explanation="System requirements (top-level) must be translated into child requirements or have tests directly linked. A gap here means a system requirement has neither children nor tests."
            value={metrics.translation}
            color="#eab308"
            active={activeDetail === "translation"}
            onMoreInfo={() => toggleDetail("translation")}
          />
          <MetricCard
            title="Incorporation"
            subtitle="How well do we understand the project?"
            description={`${metrics.product_reqs_with_tests} / ${metrics.total_product_reqs} testable requirements with tests`}
            target={metrics.incorporation_target}
            explanation="Every testable requirement (lowest level in each branch, including childless system requirements) must have at least one test. A gap here means the requirement is not yet understood well enough to verify."
            value={metrics.incorporation}
            color="#a855f7"
            active={activeDetail === "incorporation"}
            onMoreInfo={() => toggleDetail("incorporation")}
          />
          <MetricCard
            title="Test Completion"
            subtitle="How far along is the project?"
            description={`${metrics.reqs_completed} / ${metrics.product_reqs_with_tests} requirements with all tests completed`}
            target={metrics.completion_target}
            explanation="Counts testable requirements where every linked test has test_status set to done. A requirement with 5 tests needs all 5 completed to count."
            value={metrics.completion}
            color="#3b82f6"
            active={activeDetail === "completion"}
            onMoreInfo={() => toggleDetail("completion")}
          />
          <MetricCard
            title="Automation"
            subtitle="How sustainable is our verification?"
            description={`${metrics.reqs_automated} / ${metrics.product_reqs_with_tests} requirements fully automated`}
            target={metrics.automation_target}
            explanation="Counts testable requirements where every linked test is an automated or pipeline test. Requirements with any manual or checklist tests do not count."
            value={metrics.automation}
            color="#22c55e"
            active={activeDetail === "automation"}
            onMoreInfo={() => toggleDetail("automation")}
          />
        </div>
      </div>

      {reportOpen && <MetricsReportDialog projectId={projectId} projectName={projectName} onClose={() => setReportOpen(false)} />}

      {activeDetail && (
        <MetricDetailsPanel detail={metricDetails[activeDetail]} navigateTo={navigateTo} />
      )}

      {showDisciplines && discNames.length > 0 && (
        <div className="mb-7">
          <h3 className="text-[13px] uppercase text-text2 tracking-wide mb-3">Discipline Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px] border-collapse">
              <thead>
                <tr className="text-text2 text-left">
                  <th className="py-1.5 px-2 border-b border-border font-medium">Discipline</th>
                  <th className="py-1.5 px-2 border-b border-border font-medium">Incorporation</th>
                  <th className="py-1.5 px-2 border-b border-border font-medium">Completion</th>
                  <th className="py-1.5 px-2 border-b border-border font-medium">Automation</th>
                  <th className="py-1.5 px-2 border-b border-border font-medium">Total Reqs</th>
                  <th className="py-1.5 px-2 border-b border-border font-medium">With Tests</th>
                </tr>
              </thead>
              <tbody>
                {discNames.map(name => {
                  const d = disciplines[name];
                  return (
                    <tr key={name} className="text-text">
                      <td className="py-1.5 px-2 border-b border-border">{name}</td>
                      <td className="py-1.5 px-2 border-b border-border">{Math.round(d.incorporation * 100)}%</td>
                      <td className="py-1.5 px-2 border-b border-border">{Math.round(d.completion * 100)}%</td>
                      <td className="py-1.5 px-2 border-b border-border">{Math.round(d.automation * 100)}%</td>
                      <td className="py-1.5 px-2 border-b border-border">{d.total_product_reqs}</td>
                      <td className="py-1.5 px-2 border-b border-border">{d.product_reqs_with_tests}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* About section */}
      <div className="mt-4 border-t border-border pt-5">
        <h3 className="text-[13px] uppercase text-text2 tracking-wide mb-3">About These Metrics</h3>
        <p className="text-text2 text-[13px] leading-relaxed m-0 mb-3">
          A successful project transforms high-level customer requirements from the PRD into specific, tested, and verified product requirements. These four metrics track that progression: first translate (capture PRD items as product requirements), then incorporate (link tests to each product requirement), then complete (execute the tests), then automate (make verification sustainable). A project that scores well on all four has requirements that are captured, understood, verified, and sustainably maintained.
        </p>
        <p className="text-text2 text-[13px] leading-relaxed m-0">
          Test completion and automation are measured at the requirement level: a requirement with 80 unit tests counts the same as one with a single test. What matters is whether each requirement has been verified, not how many tests back it.
        </p>
      </div>
    </div>
  );
}

function previousMonthEndInAuckland(): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Pacific/Auckland", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
  const year = Number(parts.find(part => part.type === "year")?.value);
  const month = Number(parts.find(part => part.type === "month")?.value);
  return new Date(Date.UTC(year, month - 1, 0)).toISOString().slice(0, 10);
}

function MetricsReportDialog({ projectId, projectName, onClose }: { projectId: string; projectName: string; onClose: () => void }) {
  const fallbackDate = previousMonthEndInAuckland();
  const [mode, setMode] = useState<MetricReportMode>("END_OF_DAY");
  const [reportDate, setReportDate] = useState(fallbackDate);
  const [includeDisciplines, setIncludeDisciplines] = useState(false);
  const [report, setReport] = useState<MetricReport | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const dateTouched = useRef(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  const reportRequestVersion = useRef(0);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const { data: availabilityRaw } = useQuery<any>(GET_REPORTING_AVAILABILITY, { variables: { projectId }, fetchPolicy: "network-only" });
  const availability = availabilityRaw ? mapToSnakeCase(availabilityRaw.reportingAvailability) : undefined;
  const [createMetricReport] = useMutation<any>(CREATE_METRIC_REPORT);

  useEffect(() => {
    if (!dateTouched.current && availability?.default_report_date) setReportDate(availability.default_report_date);
  }, [availability?.default_report_date]);

  useEffect(() => {
    closeButton.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        reportRequestVersion.current += 1;
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      reportRequestVersion.current += 1;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const invalidate = (nextMode: MetricReportMode, nextDate = reportDate) => {
    reportRequestVersion.current += 1;
    dateTouched.current = true;
    setMode(nextMode);
    setReportDate(nextDate);
    setReport(null);
    setMessage("");
    setLoading(false);
  };

  const closeDialog = () => {
    reportRequestVersion.current += 1;
    onCloseRef.current();
  };

  const createReport = async (): Promise<MetricReport | null> => {
    const requestVersion = ++reportRequestVersion.current;
    dateTouched.current = true;
    setLoading(true);
    setMessage("");
    try {
      const requestId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      const { data } = await createMetricReport({ variables: { projectId, mode, reportDate: mode === "END_OF_DAY" ? reportDate : null, requestId } });
      if (requestVersion !== reportRequestVersion.current) return null;
      const result = mapToSnakeCase(data?.createMetricReport || {});
      if (!result.report) {
        setMessage(result.message || "This report is not available.");
        return null;
      }
      setReport(result.report);
      return result.report;
    } catch (error) {
      if (requestVersion !== reportRequestVersion.current) return null;
      setMessage(error instanceof Error ? error.message : "Could not create report.");
      return null;
    } finally {
      if (requestVersion === reportRequestVersion.current) setLoading(false);
    }
  };

  const download = async (format: "csv" | "pdf") => {
    const preparedReport = report || await createReport();
    if (!preparedReport) return;
    const anchor = document.createElement("a");
    anchor.href = `/reports/metrics/${encodeURIComponent(preparedReport.id)}.${format}?disciplines=${includeDisciplines ? "1" : "0"}`;
    anchor.click();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-labelledby="metrics-report-title" data-testid="metrics-report-dialog">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded-[12px] border border-border bg-bg2 p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <h2 id="metrics-report-title" className="m-0 text-lg text-text">Metrics report: {projectName}</h2>
          <button ref={closeButton} type="button" onClick={closeDialog} aria-label="Close report dialog" className="border-0 bg-transparent text-xl text-text2 cursor-pointer">×</button>
        </div>
        <div className="mt-5 flex flex-wrap items-end gap-3" data-testid="report-controls">
          <label className="text-xs text-text2">Report date
            <input type="date" aria-label="Report date" value={reportDate} disabled={mode === "LIVE"} min={availability?.earliest_report_date || undefined} max={availability?.latest_report_date || undefined} onChange={event => invalidate("END_OF_DAY", event.target.value)} className="mt-1 block rounded-default border border-border bg-bg px-2 py-1.5 text-text" />
          </label>
          <button type="button" onClick={() => invalidate("END_OF_DAY", availability?.default_report_date || fallbackDate)} className="px-3 py-1.5 bg-bg border border-border rounded-default text-text text-xs cursor-pointer">Last month end</button>
          <button type="button" aria-pressed={mode === "LIVE"} onClick={() => invalidate("LIVE")} className="px-3 py-1.5 bg-bg border border-border rounded-default text-text text-xs cursor-pointer">Live</button>
        </div>
        <p className="text-xs text-text2">Reporting timezone: {availability?.timezone || "Pacific/Auckland"}</p>
        <p className="text-xs text-text2">{mode === "LIVE" ? "Live creates a fresh snapshot without synchronizing Jira." : "Historical reports are stored observations captured just after the selected day. Dates without an observation are unavailable."}</p>
        {availability?.message && <p className="text-xs text-yellow">{availability.message}</p>}
        <label className="my-4 flex items-center gap-2 text-sm text-text"><input type="checkbox" checked={includeDisciplines} onChange={event => setIncludeDisciplines(event.target.checked)} />Include discipline breakdown</label>
        {message && <div role="alert" className="mb-3 text-sm text-red">{message}</div>}
        {report?.warnings?.map(warning => <div key={warning} className="mb-2 text-xs text-yellow">{warning}</div>)}
        {report && <MetricsReportContent report={report} includeDisciplines={includeDisciplines} />}
        <div className="mt-5 flex flex-wrap justify-end gap-2" data-testid="report-actions">
          <button type="button" disabled={loading} onClick={createReport} className="px-3 py-1.5 bg-bg border border-border rounded-default text-text text-xs cursor-pointer disabled:opacity-60">{loading ? "Preparing..." : "Preview"}</button>
          <button type="button" disabled={loading} onClick={() => download("csv")} className="px-3 py-1.5 bg-bg border border-border rounded-default text-text text-xs cursor-pointer disabled:opacity-40">Export CSV</button>
          <button type="button" disabled={loading} onClick={() => download("pdf")} className="px-3 py-1.5 bg-accent border border-accent rounded-default text-white text-xs cursor-pointer disabled:opacity-40">Export PDF</button>
          <button type="button" onClick={closeDialog} className="px-3 py-1.5 bg-bg border border-border rounded-default text-text text-xs cursor-pointer">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function MetricDetailsPanel({ detail, navigateTo }: { detail: MetricDetail; navigateTo: (view: import("@/types").ViewName, id: string | null) => void }) {
  return (
    <div className="mb-7 border border-border rounded-[10px] bg-bg2 p-4" data-testid="metric-details-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-[13px] uppercase text-text2 tracking-wide m-0 mb-1">{detail.title} ({detail.items.length})</h3>
          <p className="text-text2 text-[12px] m-0">{detail.summary}</p>
        </div>
      </div>
      {detail.items.length === 0 ? (
        <div className="text-[13px] text-green py-2">{detail.empty}</div>
      ) : (
        <div className="border border-border rounded-default overflow-hidden">
          {detail.items.map(r => (
            <button
              key={r.id}
              type="button"
              onClick={() => navigateTo("requirements", r.id)}
              className="w-full text-left px-3 py-2 bg-bg border-0 border-b border-border last:border-b-0 cursor-pointer transition-colors duration-150 hover:bg-bg3"
            >
              <div className="flex flex-wrap items-center gap-2 text-[13px]">
                <span className="text-accent font-semibold">{r.id}</span>
                <span className="text-text">{r.title || r.description || "Untitled requirement"}</span>
              </div>
              <div className="mt-1 text-[11px] text-text2">
                {r.level || r.requirement_level || "requirement"}
                {r.discipline ? ` · ${r.discipline}` : ""}
                {(r.tests || []).length > 0 ? ` · tests: ${(r.tests || []).map(t => t.id).join(", ")}` : " · no tests"}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Gantt Tab ----

interface GanttTabProps {
  data: import("@/types").GanttData | undefined;
  navigateTo: (view: import("@/types").ViewName, id: string | null) => void;
}

function GanttTab({ data, navigateTo }: GanttTabProps) {
  const [discFilter, setDiscFilter] = useState("");
  const [issuesOnly, setIssuesOnly] = useState(false);

  const disciplines = useMemo(() => {
    if (!data) return [];
    const set = new Set<string>();
    data.rows.forEach(r => { if (r.discipline) set.add(r.discipline); });
    return Array.from(set).sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.rows;
    if (discFilter) rows = rows.filter(r => r.discipline === discFilter);
    if (issuesOnly) {
      const issueIds = new Set<string>();
      data.issues.forEach(i => { issueIds.add(i.id); if (i.req_id) issueIds.add(i.req_id); });
      rows = rows.filter(r => {
        if (issueIds.has(r.req_id)) return true;
        if (!r.has_tests) return true;
        return r.tests.some(t => issueIds.has(t.test_id));
      });
    }
    return rows;
  }, [data, discFilter, issuesOnly]);

  if (!data) {
    return <div className="p-6 text-text2">Loading Gantt data...</div>;
  }

  const today = new Date().toISOString().substring(0, 10);
  const { rangeStart, rangeEnd, totalDays } = computeRange(filtered, today);

  return (
    <div>
      {/* Summary cards */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 mb-4">
        <DashCard value={data.total_rows} label="Leaf Requirements" />
        <DashCard value={data.with_timeline} label="With Timeline" />
        <DashCard value={data.all_done} label="All Tests Done" />
        <DashCard value={data.issue_count} label="Issues" valueColor={data.issue_count > 0 ? "text-red" : "text-green"} />
      </div>

      {/* Filters */}
      <div className="flex items-center text-[13px] pb-3">
        <label>Discipline: <select className="bg-bg2 border border-border text-text rounded px-2 py-1 text-[13px]" value={discFilter} onChange={e => setDiscFilter(e.target.value)}>
          <option value="">All</option>
          {disciplines.map(d => <option key={d} value={d}>{d}</option>)}
        </select></label>
        <label className="ml-3">
          <input type="checkbox" checked={issuesOnly} onChange={e => setIssuesOnly(e.target.checked)} /> Issues only
        </label>
        <span className="ml-4 text-text2 text-xs">Showing {filtered.length} of {data.rows.length} requirements</span>
      </div>

      {/* Issues panel */}
      {data.issues.length > 0 && (
        <div className="bg-bg2 border border-border rounded-lg px-4 py-3 mb-4">
          <h3 className="mb-2 text-sm mt-0">Issues ({data.issues.length})</h3>
          <div>
            {data.issues.slice(0, issuesOnly ? undefined : 10).map((iss, i) => (
              <div key={i} className={`text-xs py-[3px] ${iss.severity === "error" ? "text-red" : "text-yellow"}`}>
                <span className={`inline-block text-[10px] font-semibold uppercase px-[5px] py-px rounded-[3px] ${iss.severity === "error" ? "bg-red/[0.15] text-red" : "bg-yellow/[0.15] text-yellow"}`}>{iss.severity}</span>{" "}
                <span className="text-accent cursor-pointer underline" onClick={() => navigateTo("requirements", iss.req_id || iss.id)}>{iss.id}</span>{" "}
                {iss.message}
              </div>
            ))}
            {!issuesOnly && data.issues.length > 10 && (
              <div className="text-xs text-text2 mt-1">...and {data.issues.length - 10} more. Check "Issues only" to see all.</div>
            )}
          </div>
        </div>
      )}

      {/* Gantt chart */}
      <div className="gantt-chart">
        <div className="gantt-header">
          <div className="gantt-label-col">Requirement</div>
          <div className="gantt-timeline-col">
            <TimelineScale rangeStart={rangeStart} rangeEnd={rangeEnd} totalDays={totalDays} today={today} />
          </div>
        </div>

        {filtered.slice(0, 200).map(r => (
          <GanttRowView key={r.req_id} row={r} rangeStart={rangeStart} totalDays={totalDays} today={today} navigateTo={navigateTo} />
        ))}

        {filtered.length > 200 && (
          <div className="px-4 py-2 text-xs text-text2">Showing 200 of {filtered.length} rows.</div>
        )}
        {filtered.length === 0 && (
          <div className="p-6 text-text2">No rows match the current filters.</div>
        )}
      </div>
    </div>
  );
}

function GanttRowView({ row, rangeStart, totalDays, today, navigateTo }: { row: GanttRow; rangeStart: string; totalDays: number; today: string; navigateTo: (v: import("@/types").ViewName, id: string | null) => void }) {
  const statusClass = row.all_tests_done ? "gantt-row-done" : (row.has_tests ? "gantt-row-active" : "gantt-row-notest");
  const todayPct = daysBetween(rangeStart, today) / totalDays * 100;

  return (
    <div className={`gantt-row ${statusClass}`}>
      <div className="gantt-label-col">
        <span className="gantt-req-id cursor-pointer underline" onClick={() => navigateTo("requirements", row.req_id)}>{row.req_id}</span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-[4px] text-[10px] font-semibold whitespace-nowrap tracking-[0.3px] bg-[var(--badge-muted-bg)] text-text2 ml-1">{row.discipline}</span>
        {!row.has_tests && <span className="inline-block text-[10px] font-semibold uppercase px-[5px] py-px rounded-[3px] bg-bg3 text-text2 ml-1">no tests</span>}
      </div>
      <div className="gantt-timeline-col">
        <div className="gantt-bar-track">
          {row.tests.map(t => {
            const barStart = t.start || today;
            const barEnd = t.end || today;
            if (!t.start && !t.end) return null;
            let left = Math.max(0, daysBetween(rangeStart, barStart) / totalDays * 100);
            let width = Math.max(0.5, daysBetween(barStart, barEnd) / totalDays * 100);
            if (left + width > 100) width = 100 - left;
            const barClass = t.done ? "gantt-bar-done" : "gantt-bar-progress";
            const tooltip = `${t.test_id} (${t.type}/${t.suite}) ${t.status}`;
            return (
              <div key={t.test_id} className={`gantt-bar ${barClass}`} style={{ left: `${left}%`, width: `${width}%` }} title={tooltip}>
                <span className="gantt-bar-label">{t.test_id}</span>
              </div>
            );
          })}
          <div className="gantt-today" style={{ left: `${todayPct}%` }} />
        </div>
      </div>
    </div>
  );
}

function TimelineScale({ rangeStart, rangeEnd, totalDays, today }: { rangeStart: string; rangeEnd: string; totalDays: number; today: string }) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const markers: Array<{ label: string; pct: number }> = [];
  const d = new Date(rangeStart + "T00:00:00");
  d.setDate(1);
  if (d.toISOString().substring(0, 10) < rangeStart) d.setMonth(d.getMonth() + 1);
  for (let i = 0; i < 24; i++) {
    const ds = d.toISOString().substring(0, 10);
    if (ds > rangeEnd) break;
    markers.push({ label: `${months[d.getMonth()]} ${d.getFullYear()}`, pct: daysBetween(rangeStart, ds) / totalDays * 100 });
    d.setMonth(d.getMonth() + 1);
  }
  const todayPct = daysBetween(rangeStart, today) / totalDays * 100;

  return (
    <div className="gantt-scale">
      {markers.map((m, i) => (
        <span key={i} className="gantt-scale-label" style={{ left: `${m.pct}%` }}>{m.label}</span>
      ))}
      <span className="gantt-scale-today" style={{ left: `${todayPct}%` }}>Today</span>
    </div>
  );
}

// ---- Helpers ----

function daysBetween(a: string, b: string): number {
  return (new Date(b + "T00:00:00").getTime() - new Date(a + "T00:00:00").getTime()) / 86400000;
}

function offsetDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().substring(0, 10);
}

function computeRange(rows: GanttRow[], today: string) {
  let minDate = today;
  let maxDate = today;
  rows.forEach(r => {
    if (r.start && r.start < minDate) minDate = r.start;
    if (r.end && r.end > maxDate) maxDate = r.end;
    r.tests.forEach(t => {
      if (t.start && t.start < minDate) minDate = t.start;
      if (t.end && t.end > maxDate) maxDate = t.end;
    });
  });
  const rangeStart = offsetDate(minDate, -14);
  let rangeEnd = offsetDate(maxDate, 30);
  let totalDays = daysBetween(rangeStart, rangeEnd);
  if (totalDays < 30) { rangeEnd = offsetDate(rangeStart, 30); totalDays = 30; }
  return { rangeStart, rangeEnd, totalDays };
}
